/**
 *
 * Landing page for the GitLab OAuth / GitHub App installation redirect
 *
 */

import React, { useEffect, useRef, useState } from 'react';

import { Center, Spinner, Stack, Text } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  ConnectProvider,
  completeRepositoryConnect,
  connectErrorMessage,
} from 'api/onboarding/repositoryConnect';
import { PrimaryButton } from 'components/DataEntry';
import { successToast } from 'components/Toast';
import { actions as appActions } from 'containers/App/slice';

const LABELS: Record<ConnectProvider, string> = {
  gitlab: 'GitLab',
  github: 'GitHub',
};

export const RepositoryConnectCallback = () => {
  const { provider } = useParams<{ provider: ConnectProvider }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  // OAuth codes are single-use; never submit twice (e.g. StrictMode re-runs).
  const submitted = useRef(false);

  useEffect(() => {
    if (submitted.current) return;
    submitted.current = true;
    if (provider !== 'gitlab' && provider !== 'github') {
      setError('Unknown provider.');
      return;
    }
    const label = LABELS[provider];
    if (params.get('error')) {
      setError(
        params.get('error_description') ?? `${label} authorization was denied.`,
      );
      return;
    }
    if (params.get('setup_action') === 'request') {
      setError(
        'The installation is waiting for approval from a GitHub organization ' +
          'owner. Connect again once it is approved.',
      );
      return;
    }
    const code = params.get('code');
    const state = params.get('state');
    const installationId = Number(params.get('installation_id'));
    if (!code || !state || (provider === 'github' && !installationId)) {
      setError(`The ${label} response was incomplete. Start again.`);
      return;
    }
    completeRepositoryConnect(provider, {
      code,
      state,
      ...(provider === 'github' ? { installation_id: installationId } : {}),
    })
      .then(account => {
        successToast({ title: `${label} account ${account.name} connected` });
        dispatch(appActions.loadEnvAndAccounts());
        navigate('/setup/accounts', { replace: true });
      })
      .catch(e =>
        setError(
          connectErrorMessage(e, `Could not connect ${label}. Try again.`),
        ),
      );
  }, [provider, params, navigate, dispatch]);

  return (
    <Center w="full" h="full">
      {error ? (
        <Stack spacing={4} align="center" maxW="lg" textAlign="center">
          <Text>{error}</Text>
          <PrimaryButton
            onClick={() =>
              navigate(`/setup/accounts/add/${provider}`, { replace: true })
            }
          >
            BACK
          </PrimaryButton>
        </Stack>
      ) : (
        <Stack spacing={3} align="center">
          <Spinner color="primary" />
          <Text>Connecting {provider ? LABELS[provider] : ''}…</Text>
        </Stack>
      )}
    </Center>
  );
};
